import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack, withShade} from '@dropbox/dig-foundations';
import type {Employee, WorkstreamBasicWithSummary} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {StatusButtonIcon} from 'components/DSYS/StatusButtonIcon';
import {useEmployeesBatch} from 'hooks/useEmployee';
import {ReactNode, useEffect} from 'react';
import {getWorkstreamUpdateVisualStatus} from 'views/workstreams/utils';

import {PinToggleButton} from './PinToggleButton';
import {
  pickRecentItems,
  RecentStripItem,
  recentStripKey,
  useRecentWorkstreamRows,
} from './recentsStripData';
import {useHoverReveal} from './useHoverReveal';
import {useScrollEdgeFade} from './useScrollEdgeFade';
import {viaState} from './workstreamsAnalytics';
import {getFreshness} from './WorkstreamScanList';
import type {PinnedItem} from './WorkstreamsSearch';

const CARD_WIDTH_PX = 200;
// The leading slot (status dot or avatar): one name line tall, so it sits on
// the name; at least the dot's width.
const DOT_SLOT_PX = 12;
const NAME_LINE_PX = 20;
// While the pin shows, the name fades out before it (the pin's 24px button sits
// 4px in from the card's edge, over the name's last 16px), so a long name
// keeps its full width otherwise.
const NAME_UNDER_PIN_MASK =
  'linear-gradient(to right, #000 calc(100% - 40px), transparent calc(100% - 20px))';
const NAME_UNDER_PIN_STYLE = {maskImage: NAME_UNDER_PIN_MASK, WebkitMaskImage: NAME_UNDER_PIN_MASK};

// Tinted, not outlined: quick links above the list, not more cards.
const cardClass = atoms({
  display: 'flex',
  alignItems: 'flex-start',
  backgroundColor: 'Opacity Surface',
  borderRadius: 'Medium',
  paddingY: '8',
  paddingX: '12',
  textDecoration: 'none',
  color: 'Text Base',
});

const driOf = (row?: WorkstreamBasicWithSummary) =>
  row?.employee_associations.find(({dri}) => dri === 'dri')?.employee;

// One link per card (the shade is its hover): the leading dot / avatar on the
// name, then a subtitle — a blank line without one, so cards keep one height.
// The pin sits over the name's end, top right: shown on hover, or always once
// pinned, with the name fading out under it.
const RecentCard = ({
  name,
  leading,
  subtitle,
  href,
  pinned,
  onTogglePin,
}: {
  name: string;
  leading: ReactNode;
  subtitle: ReactNode;
  href: string;
  pinned: boolean;
  onTogglePin: () => void;
}) => {
  const {regionProps, revealStyle, revealed} = useHoverReveal();
  const pinShown = pinned || revealed;
  return (
    <Box {...regionProps} position="relative" style={{flex: `0 0 ${CARD_WIDTH_PX}px`, minWidth: 0}}>
      <Link
        to={href}
        // A workstream's recent opens its card, not the full page (see parseDetailLink).
        data-own-link
        state={viaState('recents_strip')}
        hasNoUnderline
        monochromatic
        {...withShade({duration: 'Instant', className: cardClass, style: {gap: 8, minWidth: 0}})}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{minWidth: DOT_SLOT_PX, height: NAME_LINE_PX, flexShrink: 0}}
        >
          {leading}
        </Box>
        <Box display="flex" flexDirection="column" style={{minWidth: 0}}>
          <Text size="small" isBold style={pinShown ? NAME_UNDER_PIN_STYLE : undefined}>
            <Truncate lines={1}>{name}</Truncate>
          </Text>
          <Text size="small" color="faint">
            <Truncate lines={1}>{subtitle || '\u00a0'}</Truncate>
          </Text>
        </Box>
      </Link>
      <Box position="absolute" style={{top: 4, right: 4, ...(pinned ? undefined : revealStyle)}}>
        <PinToggleButton pinned={pinned} onToggle={onTogglePin} />
      </Box>
    </Box>
  );
};

// A workstream: its status dot, then "DRI · 6w ago". Its name falls back to
// the one stored with the recent until the row loads.
const workstreamCard = (
  item: Extract<RecentStripItem, {type: 'workstream'}>,
  row?: WorkstreamBasicWithSummary
) => {
  const freshness = row ? getFreshness(row) : undefined;
  const dri = driOf(row)?.name;
  return {
    name: row?.name ?? item.name,
    leading: row && (
      <StatusButtonIcon
        status={getWorkstreamUpdateVisualStatus(row.updates?.[0]?.status)}
        size="xsmall"
        includeArchived
      />
    ),
    subtitle: (dri || freshness) && (
      <>
        {dri ?? ''}
        {dri && freshness ? ' · ' : ''}
        {freshness && (
          <Text size="small" color="faint">
            {freshness.label}
          </Text>
        )}
      </>
    ),
  };
};

// A person: their avatar, then their title, from the stored recent until
// their profile loads.
const personCard = (item: Extract<RecentStripItem, {type: 'person'}>, employee?: Employee) => ({
  name: employee?.name ?? item.name,
  leading: (
    <Avatar
      size="xsmall"
      user={{
        name: employee?.name ?? item.name,
        ldap: item.ldap,
        profile_photo_url: employee?.profile_photo_url ?? item.profilePhoto,
      }}
    />
  ),
  subtitle: employee?.role,
});

/**
 * /workstreams: the workstreams and people you last opened, as one
 * scrolling row of cards over the My workstreams list, pinned first. The page
 * owns the history and pins (the rail's recents); `hrefFor` opens a card the
 * way a rail click does. Nothing shows until you've opened one. Loaded
 * workstream names that differ from the saved ones go to `onNamesLoaded`, so
 * the next load starts right.
 */
export const RecentsStrip = ({
  items,
  hrefFor,
  onTogglePin,
  onNamesLoaded,
}: {
  items: PinnedItem[];
  hrefFor: (item: RecentStripItem) => string;
  onTogglePin: (item: RecentStripItem) => void;
  onNamesLoaded: (names: Map<number, string>) => void;
}) => {
  const recents = pickRecentItems(items);
  const workstreamIds = recents.flatMap((item) => (item.type === 'workstream' ? [item.id] : []));
  // Sorted, so pin toggles reorder without a refetch.
  const ldaps = recents.flatMap((item) => (item.type === 'person' ? [item.ldap] : [])).sort();
  const {data: rows, dataUpdatedAt} = useRecentWorkstreamRows(workstreamIds);
  const {employees} = useEmployeesBatch({ldaps, enabled: ldaps.length > 0});
  const staleNames = rows
    ? recents.flatMap((item) => {
        if (item.type !== 'workstream') return [];
        const name = rows.get(item.id)?.name;
        return name && name !== item.name ? [[item.id, name] as const] : [];
      })
    : [];
  useEffect(() => {
    if (staleNames.length > 0) onNamesLoaded(new Map(staleNames));
    // Only names this tab just fetched get saved. Keyed on the stored items
    // instead, a tab still holding older rows would write them back over
    // another tab's newer names, and the two would trade them forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);
  const scrollFade = useScrollEdgeFade<HTMLDivElement>(recents.length);
  if (recents.length === 0) return null;

  return (
    // The label lines up with the cards below; the row runs the pane's width, so
    // tiles start on the cards' edge and scroll out across the gutter.
    <Stack gap="8">
      <Layout.MaxWidth width="reading" inset>
        <Text size="small" isBold color="subtle">
          Recently viewed
        </Text>
      </Layout.MaxWidth>
      <Layout.InsetScroll
        ref={scrollFade.ref}
        onScroll={scrollFade.onScroll}
        style={{gap: 8, ...scrollFade.style}}
      >
        {recents.map((item) => (
          <RecentCard
            key={recentStripKey(item)}
            {...(item.type === 'person'
              ? personCard(
                  item,
                  employees.find((employee) => employee.ldap === item.ldap)
                )
              : workstreamCard(item, rows?.get(item.id)))}
            href={hrefFor(item)}
            pinned={Boolean(item.pinned)}
            onTogglePin={() => onTogglePin(item)}
          />
        ))}
      </Layout.InsetScroll>
    </Stack>
  );
};
